import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useMemo } from "react";

import type { NodeConfig } from "@skilitics/nautilus-core-designer-data";

import {
  designerNodeConfigSelector,
  designerNodeTypeIdSelector,
  parentContextSelector,
  useMutateNodeDesigns,
  useNautilusSelector,
} from "../../state/index.ts";
import type { EventConditionConfigurerNodeProp } from "../../types/index.ts";

const nodeConfigurerPropsSelector = createSelector(
  [designerNodeTypeIdSelector, designerNodeConfigSelector, parentContextSelector],
  (typeId, config, parentContext) => ({ typeId, config, parentContext }),
);

export const useNodeTriggerConditionProp = (nodeId: number): EventConditionConfigurerNodeProp => {
  const { update } = useMutateNodeDesigns();

  const { typeId, config, parentContext } = useNautilusSelector((state) =>
    nodeConfigurerPropsSelector(state, { nodeId }),
  );

  const onConfigChange = useCallback(
    (newConfig: Partial<NodeConfig>) => {
      update(nodeId, newConfig as NodeConfig);
    },
    [nodeId, update],
  );

  return useMemo(
    () => ({ id: nodeId, typeId, config, parentContext, onConfigChange }),
    [nodeId, typeId, config, parentContext, onConfigChange],
  ) as EventConditionConfigurerNodeProp;
};
